import { useEffect, useState, useRef } from 'react'
import { appConfig } from '../config'

export function useTelegram() {
  const tgRef = useRef<TelegramWebApp | undefined>(undefined)
  const [initData, setInitData] = useState<string | undefined>()
  const [isTelegram, setIsTelegram] = useState(false)

  useEffect(() => {
    let currentTg: TelegramWebApp | undefined

    if (appConfig.useTelegramMock) {
      const mock = {
        initData: appConfig.mockInitData,
        initDataUnsafe: { user: appConfig.mockTelegramUser },
        ready: () => undefined,
        expand: () => undefined,
        close: () => undefined,
        MainButton: {
          setText: () => undefined,
          show: () => undefined
        },
        disableVerticalSwipes: () => undefined,
        web_app_setup_swipe_behavior: () => undefined
      } satisfies TelegramWebApp

      tgRef.current = mock
      currentTg = mock
      setInitData(mock.initData)
      setIsTelegram(true)

      return () => {
        currentTg = undefined
        tgRef.current = undefined
      }
    }

    const init = () => {
      try {
        const t = window.Telegram?.WebApp
        if (t && t !== currentTg) {
          currentTg = t
          tgRef.current = t
          t.ready()
          t.disableVerticalSwipes?.()
          t.web_app_setup_swipe_behavior?.({ isVerticalSwipesEnabled: false })
          t.expand?.()
          setInitData(t.initData)
          setIsTelegram(true)
        } else if (!t) {
          if (currentTg) console.warn('Telegram.WebApp not found')
          currentTg = undefined
          tgRef.current = undefined
          setIsTelegram(false)
        }
      } catch (error) {
        console.error('Error initializing Telegram WebApp:', error)
        currentTg = undefined
        tgRef.current = undefined
        setIsTelegram(false)
      }
    }

    try {
      init()
      window.addEventListener('load', init)
      const interval = setInterval(() => {
        if (window.Telegram?.WebApp !== currentTg) init()
      }, 1000)

      return () => {
        window.removeEventListener('load', init)
        clearInterval(interval)
        tgRef.current = undefined
      }
    } catch (error) {
      console.error('Error setting up Telegram WebApp listeners:', error)
      return () => {
        tgRef.current = undefined
      }
    }
  }, [])

  return {
    tg: tgRef.current,
    initData,
    isTelegram,
    user:
      tgRef.current?.initDataUnsafe?.user ??
      (appConfig.useTelegramMock ? appConfig.mockTelegramUser : undefined),
    expand: () => {
      try {
        tgRef.current?.expand?.()
      } catch (error) {
        console.error('Error expanding Telegram WebApp:', error)
      }
    },
    close: () => {
      try {
        tgRef.current?.close?.()
      } catch (error) {
        console.error('Error closing Telegram WebApp:', error)
      }
    },
    closeApp: () => {
      try {
        tgRef.current?.close?.()
      } catch (error) {
        console.error('Error closing app:', error)
      }
    },
    openTelegramLink: (url: string) => {
      try {
        tgRef.current?.openTelegramLink?.(url)
      } catch (error) {
        console.error('Error opening Telegram link:', error)
      }
    },
    onEvent: (event: string, cb: TelegramWebAppEventCallback) => {
      try {
        tgRef.current?.onEvent?.(event, cb)
      } catch (error) {
        console.error(`Error adding event listener for ${event}:`, error)
      }
    },
    offEvent: (event: string, cb: TelegramWebAppEventCallback) => {
      try {
        tgRef.current?.offEvent?.(event, cb)
      } catch (error) {
        console.error(`Error removing event listener for ${event}:`, error)
      }
    },
    showMainButton: (text: string) => {
      if (!tgRef.current) return
      try {
        tgRef.current.MainButton.setText(text)
        tgRef.current.MainButton.show()
      } catch (error) {
        console.error('Error showing main button:', error)
      }
    }
  }
}
